// Utility function for Facebook Pixel tracking
type PixelEventData = Record<string, string | number | boolean | undefined>
type EventType = 'standard' | 'custom'

/**
 * Track an event with Facebook Pixel
 * @param eventName The name of the event to track
 * @param eventData Optional data to include with the event
 * @param type Whether this is a standard or custom event (defaults to standard)
 * @returns Promise that resolves when tracking is complete
 */
export const trackPixel = (
  eventName: string,
  eventData?: PixelEventData,
  type: EventType = 'standard',
): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        if (type === 'custom') {
          ReactPixel.trackCustom(eventName, eventData)
        } else {
          ReactPixel.track(eventName, eventData)
        }
        resolve()
      })
      .catch(() => {
        // Silently fail if FB Pixel cannot be loaded
        resolve()
      })
  })
}

// Backwards compatibility
export const trackPixelEvent = (
  eventName: string,
  eventData?: PixelEventData,
): Promise<void> => trackPixel(eventName, eventData, 'custom')

export const trackPixelStandardEvent = (
  eventName: string,
  eventData?: PixelEventData,
): Promise<void> => trackPixel(eventName, eventData)
